/**
 * metrics-nested-levels.ts — 多维分组「逐级嵌套」的分级取数。
 *
 * 分组维度 [d1, d2, d3] 在表里按 d1 → d2 → d3 逐级展开；每一级各取一份「该前缀的分组结果」
 * （group_by = dims.slice(0, 级数)），父行是该前缀的真实聚合（去重人数不能由子行相加），
 * 只有展开到某一级才发这一级的请求。第 1 级由页面自己的查询承担，这里只管第 2 级起。
 */

import { useQueries } from '@tanstack/react-query';

export interface NestedLevelResult<TData> {
  data: TData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

export function useMetricsNestedLevels<TData>(args: {
  /** 每一级的 group_by（下标 0 = 第 2 级，即 dims.slice(0, 2)） */
  levelGroupBys: ReadonlyArray<readonly string[]>;
  /** 前几个「更深级」要发请求（0 = 都不发） */
  activeDeeperLevels: number;
  queryKeyOf: (groupBy: readonly string[]) => readonly unknown[];
  fetchLevel: (groupBy: readonly string[]) => Promise<TData>;
  enabled: boolean;
}): Array<NestedLevelResult<TData>> {
  const results = useQueries({
    queries: args.levelGroupBys.map((groupBy, index) => ({
      queryKey: args.queryKeyOf(groupBy) as unknown[],
      enabled: args.enabled && index < args.activeDeeperLevels,
      queryFn: () => args.fetchLevel(groupBy),
    })),
  });
  return results.map((result) => ({
    data: result.data,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isError: result.isError,
  }));
}
